import { createContext, use } from "react";

import * as m from "../../model/model";
import { noop } from "../../utils/noop";

import PricePlanFeature = m.Entities.PricePlanFeature;
import BokioPlan = m.Entities.BokioPlan;
type PricePlanFeatureInfo = m.Bokio.Common.Contract.ViewData.PricePlanFeatureInfo;
type AddonsDto = m.Bokio.Billing.Contract.Models.Subscriptions.AddonsDto;

export { PricePlanFeature };

interface BokioPlanOrAddonNeeded {
	plansNeeded: BokioPlan[];
	/** If set it exists as addon otherwise not */
	addonNeeded: m.Bokio.Common.Contract.ViewData.PricePlanAddonFeatureMapping | undefined;
}

export type PricePlanFeatureContextValue = {
	refreshPricePlanFeatures: () => Promise<void>;
	isPricePlanFeatureEnabled: (feature: PricePlanFeature) => boolean;
	isPricePlanFeatureInTrial: (feature: PricePlanFeature) => boolean;
	isPricePlanFeatureAvailableInTrial: (feature: PricePlanFeature) => boolean;
	getPlansOrAddonNeededForPricePlanFeature: (args: {
		feature: PricePlanFeature;
		currentPlan: BokioPlan | undefined;
		companyInTrial: boolean;
	}) => BokioPlanOrAddonNeeded;
	getAddonForPricePlanFeature: (feature: PricePlanFeature) => AddonsDto | undefined;
	isLoading: boolean;
};

export const PricePlanFeatureContext = createContext<PricePlanFeatureContextValue>({
	refreshPricePlanFeatures: noop,
	isPricePlanFeatureEnabled: noop,
	getPlansOrAddonNeededForPricePlanFeature: noop,
	isPricePlanFeatureInTrial: noop,
	isPricePlanFeatureAvailableInTrial: noop,
	getAddonForPricePlanFeature: noop,
	isLoading: false,
});

export const usePricePlanFeatureContext = () => {
	const context = use(PricePlanFeatureContext);

	return context;
};

export const createPricePlanFeatureContextValue = ({
	refreshPricePlanFeatures,
	pricePlanFeatures,
	currentPlanAddons,
	isLoading,
}: {
	refreshPricePlanFeatures: () => Promise<void>;
	pricePlanFeatures: PricePlanFeatureInfo[];
	currentPlanAddons: AddonsDto[];
	isLoading: boolean;
}): PricePlanFeatureContextValue => {
	const isPricePlanFeatureEnabled = (feature: PricePlanFeature) => {
		const pricePlanFeature = pricePlanFeatures.find(pricePlanFeature => pricePlanFeature.Feature == feature);
		return pricePlanFeature?.Available ?? false;
	};

	const isPricePlanFeatureInTrial = (feature: PricePlanFeature) => {
		const pricePlanFeature = pricePlanFeatures.find(pricePlanFeature => pricePlanFeature.Feature == feature);
		return (pricePlanFeature?.InTrial && pricePlanFeature?.AvailableInTrial) ?? false;
	};

	const isPricePlanFeatureAvailableInTrial = (feature: PricePlanFeature) => {
		const pricePlanFeature = pricePlanFeatures.find(pricePlanFeature => pricePlanFeature.Feature == feature);
		return pricePlanFeature?.AvailableInTrial ?? false;
	};

	const getAddonForPricePlanFeature = (feature: PricePlanFeature) => {
		const pricePlanFeature = pricePlanFeatures.find(pricePlanFeature => pricePlanFeature.Feature === feature);
		const addonMapping = pricePlanFeature?.AvailableAsAddonIn[0];
		if (!addonMapping || addonMapping.AddonType === m.Bokio.Common.Contract.ViewData.PricePlanAddonType.None) {
			return undefined;
		}

		return currentPlanAddons.find(addon => String(addon.Type) === String(addonMapping.AddonType));
	};

	const getPlansOrAddonNeededForPricePlanFeature = ({
		feature,
		currentPlan,
		companyInTrial,
	}: {
		feature: PricePlanFeature;
		currentPlan: BokioPlan | undefined;
		companyInTrial: boolean;
	}): BokioPlanOrAddonNeeded => {
		const pricePlanFeature = pricePlanFeatures.find(pricePlanFeature => pricePlanFeature.Feature == feature);
		if (!pricePlanFeature) {
			return { plansNeeded: [], addonNeeded: undefined };
		}

		// All plans included here despite all plans not being available for each market. The pricePlanFeature.AvailableIn only contains the plans available for the market.
		// They are grouped to be able to know which plans belongs together. Free is available in all groups but we pick the first match so this will not
		// work well if we have multiple plan groups ongoing at the same time where both have blocks in the product.
		const planGroupsSortOrder = [
			[BokioPlan.Free, BokioPlan.Basic_2025, BokioPlan.Premium_2025, BokioPlan.Business_2025, BokioPlan.Plus_2025],
			[BokioPlan.Free, BokioPlan.Premium, BokioPlan.Pro],
		];

		// This is a special case because we are using the old Premium plan when you are in trial for the new plans.
		// This hack should really be removed but is needed to get the proper plans needed for the feature.
		const currentAdjustedPlan =
			companyInTrial && (currentPlan === BokioPlan.Premium || currentPlan === BokioPlan.Trial_2025)
				? BokioPlan.Business_2025
				: currentPlan;

		const planSortingOrder: BokioPlan[] =
			(currentAdjustedPlan
				? planGroupsSortOrder.find(group => group.includes(currentAdjustedPlan))
				: planGroupsSortOrder[0]) ?? [];

		const plansNeeded = pricePlanFeature.AvailableIn.map(plan => ({ plan, order: planSortingOrder.indexOf(plan) }))
			.filter(x => x.order >= 0)
			.sort((a, b) => a.order - b.order)
			.map(x => x.plan);

		const addonsNeeded = pricePlanFeature.AvailableAsAddonIn.filter(a => a.AvailableAsAddonIn === currentPlan);
		const addonNeeded = !companyInTrial && addonsNeeded.length ? addonsNeeded[0] : undefined;

		return {
			plansNeeded,
			addonNeeded,
		};
	};

	return {
		refreshPricePlanFeatures,
		isPricePlanFeatureEnabled,
		isPricePlanFeatureInTrial,
		isPricePlanFeatureAvailableInTrial,
		getPlansOrAddonNeededForPricePlanFeature,
		getAddonForPricePlanFeature,
		isLoading,
	};
};
